import React, { useEffect, useState } from 'react';
import {
  Button,
  Snackbar,
  makeStyles,
  Box,
  Typography,
  ListItem,
  List,
  ListItemText,
  Theme,
  CircularProgress,
} from '@material-ui/core';
import { Copy } from 'react-feather';
import { colors, DefaultButton } from 'design-system';
import { v4 as uuidv4 } from 'uuid';
import NetworkClient from 'src/services/NetworkClient';
import defaultQrValue from './defaultQrValue';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: '20px',
    marginLeft: '272px',
    marginRight: '255px',
    [theme.breakpoints.up('lg')]: {
      justifyContent: 'space-between',
    },
    [theme.breakpoints.up('xl')]: {
      justifyContent: 'space-evenly',
    },
  },
  snackbarButton: {
    background: colors.madison,
    color: colors.bright_turquoise,
    boxShadow: 'none',
  },
  snackbarContent: {
    background: colors.madison,
  },
  itemText: {
    // delete when migrate to design system v2 BC1/Regular
    fontWeight: 400,
    fontSize: '15px',
    lineHeight: '22px',
    color: '#4A4B4E', // neutral600
  },
  title: {
    marginBottom: '25px',
    // delete when migrate to design system v2 Heading/H5/Black
    fontWeight: 900,
    fontSize: '23px',
    lineHeight: '30px',
  },
  copyIcon: {
    marginRight: '5px',
  },
}));

const itemList: { icon: JSX.Element; text: JSX.Element }[] = [
  {
    icon: <>1</>,
    text: <>Procura estar cerca del auto para poder tomar las fotos.</>,
  },
  {
    icon: <>2</>,
    text: <>No olvides tener a mano tu registro vehicular.</>,
  },
  {
    icon: <>3</>,
    text: (
      <>
        Escanea el código QR para iniciar la inspección o puedes <br /> copiar
        el link y enviarlo a tu celular.
      </>
    ),
  },
];

const QRSection = (): JSX.Element => {
  const classes = useStyles();
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const [dataQR, setDataQR] = useState<string>(defaultQrValue());

  useEffect(() => {
    (async (): Promise<void> => {
      const response = await NetworkClient({
        method: 'get',
        url: `inspections/qr/codes?url=${window.location.href}`,
        responseType: 'blob',
      });
      setDataQR(URL.createObjectURL(response.data));
    })();
  }, []);

  // Snackbar functions
  const closeSnackbar = (): void => setShowSnackbar(false);
  const openSnackbar = (): void => setShowSnackbar(true);

  return (
    <Box display="flex" className={classes.container}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        style={{ marginTop: '-100px' }}
      >
        <Typography className={classes.title}>
          ¡Realizar la inspección es muy fácil!
        </Typography>
        <List data-testid="list-elements">
          {itemList.map(({ icon, text }) => (
            <ListItem disableGutters key={uuidv4()} alignItems="flex-start">
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: colors.bright_turquoise,
                  display: 'flex',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 16,
                  fontWeight: 700,
                  marginRight: '8px',
                }}
              >
                {icon}
              </Box>
              <ListItemText
                primary={text}
                primaryTypographyProps={{ className: classes.itemText }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box style={{ position: 'relative' }}>
          <img
            style={{ marginBottom: '19.23px' }}
            src="svg/QR_illustration.svg"
            alt="QR ilustración"
          />
          {!dataQR ? (
            <CircularProgress
              data-testid="circular-progress"
              style={{
                position: 'absolute',
                top: '156px',
                left: '190px',
              }}
            />
          ) : (
            <img
              style={{
                position: 'absolute',
                top: 116,
                left: 149,
                height: '128px',
                width: '128px',
              }}
              src={dataQR}
              alt="código QR"
              data-testid="qr-code"
            />
          )}
        </Box>
        <DefaultButton
          variant="outlined"
          id="sales-web-inspection-button-copy-link"
          data-testid="buton-copy-link"
          forcedWidth={289}
          onClick={async (): Promise<void> => {
            await navigator.clipboard.writeText(`${window.location.href}`);
            openSnackbar();
          }}
        >
          <Copy width={23} height={24} className={classes.copyIcon} /> copiar
          link de inspección
        </DefaultButton>
        <Snackbar
          data-testid="snackbar"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          autoHideDuration={5000}
          ContentProps={{ className: classes.snackbarContent }}
          open={showSnackbar}
          message="El link de inspección ha sido copiado."
          onClose={closeSnackbar}
          action={
            <Button
              className={classes.snackbarButton}
              color="secondary"
              variant="contained"
              data-testid="close-snackbar-button"
              size="small"
              onClick={closeSnackbar}
            >
              Entendido
            </Button>
          }
        />
      </Box>
    </Box>
  );
};

export default QRSection;
